import React, { useState, useCallback } from 'react';
import { useAuth } from '../../../auth/components/AuthProvider';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../../../../firebase';
import AdminSidebar from './AdminSidebar';

const SIDEBAR_COLLAPSED_KEY = 'admin-sidebar-collapsed';

function readSidebarCollapsed() {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

function writeSidebarCollapsed(collapsed) {
  try {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0');
  } catch {
    /* ignore */
  }
}

/**
 * Shell del admin: sidebar (desktop, colapsable) + drawer (mobile) + topbar + área de contenido.
 * Cierra el drawer al navegar (onNavigate).
 * Desktop: sidebar se puede ocultar para expandir el contenido; preferencia en localStorage.
 */
export default function AdminLayoutShell({ children }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readSidebarCollapsed);
  const { user } = useAuth();
  const navigate = useNavigate();

  const closeDrawer = () => setDrawerOpen(false);

  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      writeSidebarCollapsed(next);
      return next;
    });
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (e) {
      console.error('Error al cerrar sesión:', e);
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-100">
      {/* Sidebar desktop: oculto cuando colapsado para que main ocupe todo el ancho */}
      {!sidebarCollapsed && (
        <div className="hidden md:block flex-shrink-0">
          <AdminSidebar onNavigate={closeDrawer} />
        </div>
      )}

      {/* Drawer móvil */}
      {drawerOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-30 md:hidden"
            onClick={closeDrawer}
            aria-hidden="true"
          />
          <div className="fixed inset-y-0 left-0 z-40 w-[260px] md:hidden shadow-xl">
            <AdminSidebar onNavigate={closeDrawer} />
          </div>
        </>
      )}

      {/* Contenido + topbar */}
      <div className="flex-1 flex flex-col min-w-0 min-h-screen">
        <header className="flex items-center justify-between gap-2 h-14 px-4 md:px-6 bg-white border-b border-slate-200 flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            {/* Mobile: abrir drawer */}
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="md:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-100 flex-shrink-0"
              aria-label="Abrir menú"
            >
              <span className="text-xl">☰</span>
            </button>
            {/* Desktop: colapsar / expandir sidebar */}
            <button
              type="button"
              onClick={toggleSidebarCollapsed}
              className="hidden md:inline-flex items-center gap-2 px-3 py-2 rounded-lg text-slate-600 hover:bg-slate-100 border border-slate-200 flex-shrink-0 text-sm font-medium"
              aria-label={sidebarCollapsed ? 'Mostrar menú lateral' : 'Ocultar menú lateral'}
              title={sidebarCollapsed ? 'Mostrar menú lateral' : 'Ocultar menú lateral'}
            >
              <span className="text-lg" aria-hidden>
                {sidebarCollapsed ? '☰' : '◀'}
              </span>
              <span className="hidden lg:inline">
                {sidebarCollapsed ? 'Mostrar menú' : 'Ocultar menú'}
              </span>
            </button>
          </div>
          <div className="flex-1 min-w-0 font-semibold text-slate-800 truncate text-center md:text-left">
            Administración
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <span className="text-sm text-slate-600 truncate max-w-[140px] hidden sm:inline" title={user?.email}>{user?.email}</span>
            <button
              type="button"
              onClick={handleLogout}
              className="px-3 py-1.5 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded-lg transition-colors"
              aria-label="Cerrar sesión"
            >
              Cerrar sesión
            </button>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-6 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
